/**
 * Резерв сумнівних боргів — список (канон Норми #29, 2026-08-22):
 * .card + InfiniteDocGrid (серверна пагінація/сортування), фільтри рядком
 * під назвою — усі виконуються сервером (орг/період).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  doubtfulDebtReserves,
  type DoubtfulDebtReserveListItem,
} from '@/api/doubtfulDebtReserves';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DoubtfulDebtReservesPage() {
  const navigate = useNavigate();
  // Шаблон проекту (2026-08-19): орг слідує за шапкою, дати — з верхнього
  // фільтра періоду.
  const [orgId, setOrgId] = useOrgFilter();
  const { dateFrom: gFrom, dateTo: gTo } = useDefaultPeriod();
  const [fltFrom, setFltFrom] = useState(gFrom?.slice(0, 10) ?? '');
  const [fltTo, setFltTo] = useState(gTo?.slice(0, 10) ?? '');
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgList.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgList.data]);

  const { datasource, total } = useInfiniteGridDatasource<DoubtfulDebtReserveListItem>(
    (limit, offset, sort) => doubtfulDebtReserves.list({
      organization_id: orgId || undefined,
      date_from: fltFrom || undefined,
      date_to: fltTo || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, fltFrom, fltTo],
    { attachmentsType: 'doc.doubtful_debt_reserves' },
  );

  const cols = useMemo<ColDef<DoubtfulDebtReserveListItem>[]>(() => [
    { field: 'document_number', headerName: '№', width: 150 },
    {
      field: 'document_date', headerName: 'Дата', width: 170,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', flex: 1, minWidth: 190,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    {
      field: 'total_amount', headerName: 'Сума резерву', width: 160,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 130,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => p.data ? (p.value ? '✓ так' : 'ні') : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Резерв сумнівних боргів<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/doubtful-debt-reserves/new')}>
          + Новий документ
        </button>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 200, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltFrom} onChange={setFltFrom} />
        </div>
        <span style={{ color: 'var(--muted)' }}>—</span>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltTo} onChange={setFltTo} />
        </div>
        {(fltFrom || fltTo) && (
          <button onClick={() => { setFltFrom(''); setFltTo(''); }}>скинути</button>
        )}
      </div>
      <InfiniteDocGrid<DoubtfulDebtReserveListItem>
        copyRow={(r) => navigate(`/doubtful-debt-reserves/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => doubtfulDebtReserves.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="doubtful-debt-reserves"
        onRowDoubleClicked={(r) => navigate(`/doubtful-debt-reserves/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
